import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useInView } from "react-intersection-observer";
import Layout from "./Layout";
import HomeCenterBar from "../ui/compoP/CenterBars/HomeCenterBar.jsx";
import { getItems } from "../services/itemsServices.js";
import SvgIcn from "../data/IconCompo.js";
import HomeLeftBar from "../ui/compoP/LeftBars/HomeLeftBar.jsx";
import { LazyItemCard } from "../ui/compoP/LazyItemCard.jsx";
import { getMyFavorites } from "../services/pbFavorites.js";
import { resolveFavoriteItems, favoriteIdMatches } from "../services/favoriteResolver.js";

function Apps() {
  const [formData, setFormData] = useState({});
  const [totalItemsNumber, setTotalItemsNumber] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [items, setItems] = useState([]);
  const [favPbIds, setFavPbIds] = useState([]);
  const [favsItems, setFavsItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const contentRef = useRef(null);
  const [loadMoreScrollRoot, setLoadMoreScrollRoot] = useState(null);
  const csvAppendBusyRef = useRef(false);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const ITEMS_PER_PAGE = 60;

  const setContentWrapperNode = useCallback((node) => {
    contentRef.current = node;
    setLoadMoreScrollRoot(node);
  }, []);

  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
    threshold: 0,
    root: loadMoreScrollRoot ?? undefined,
    rootMargin: "0px 0px 480px 0px",
  });

  const handleFormChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.name]: e.value,
    }));
  };

  useEffect(() => {
    localStorage.setItem("mode", "apps");
    setTimeout(() => {
      localStorage.setItem("items", JSON.stringify(filteredItems));
    }, 500);
  }, [filteredItems]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleFormChange({ name: "search", value: debouncedSearch });
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedSearch]);

  const handleClearFilters = () => {
    setFilteredItems(items);
    setFormData({});
    setDebouncedSearch("");
  };

  useEffect(() => {
    handleClearFilters();
  }, []);

  const toogleLike = (item) => {
    const wasFavorite = favPbIds.some((fid) => favoriteIdMatches(fid, item));
    setFavPbIds((prev) => {
      if (wasFavorite) {
        return prev.filter((fid) => !favoriteIdMatches(fid, item));
      }
      return [...prev, item._id];
    });
    setFavsItems((prev) => {
      if (wasFavorite) {
        return prev.filter((f) => f._id !== item._id);
      }
      if (prev.some((f) => f._id === item._id)) return prev;
      return [...prev, { ...item, __pbFavoriteKeys: [item._id] }];
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const grouped = await getMyFavorites();
        const favIds = grouped.myApps || [];
        setFavPbIds(favIds);
        const result = await getItems("apps", "ALL", "", [], [], 1, 1000);
        const list = result?.items || [];
        const resolved = await resolveFavoriteItems(favIds, list);
        setFavsItems(resolved);
      } catch (err) {
        console.log(err);
        setFavPbIds([]);
        setFavsItems([]);
      }
    })();
  }, []);

  useEffect(() => {
    setPage(1);
    filterItems(
      "apps",
      formData.tag,
      formData.search,
      [formData.check],
      [formData.radio],
      1,
      ITEMS_PER_PAGE
    );
  }, [formData]);

  const filterItems = (
    mode,
    tag,
    search,
    platform,
    price,
    currentPage,
    limit
  ) => {
    if (tag === "Favs") {
      setLoading(true);
      if (currentPage === 1 && contentRef.current) {
        contentRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      let filtered = [...favsItems];

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.name?.toLowerCase().includes(searchLower) ||
            item.description?.[0]?.toLowerCase().includes(searchLower) ||
            item.tags?.some((t) => t.toLowerCase().includes(searchLower))
        );
      }

      if (platform && platform.length > 0 && platform[0]) {
        const pf = platform[0];
        filtered = filtered.filter((item) =>
          item.platform?.some(
            (p) => p === pf || p?.toLowerCase?.() === pf?.toLowerCase?.()
          )
        );
      }

      if (price && price.length > 0 && price[0]) {
        filtered = filtered.filter((item) => item.price === price[0]);
      }

      const startIndex = (currentPage - 1) * limit;
      const paginatedItems = filtered.slice(startIndex, startIndex + limit);
      setTotalItemsNumber(filtered.length);

      if (currentPage === 1) {
        setFilteredItems(paginatedItems);
      } else {
        setFilteredItems((prev) => [...prev, ...paginatedItems]);
      }

      setHasMore(paginatedItems.length === limit);
      setLoading(false);
      return;
    }

    if (currentPage > 1) {
      if (csvAppendBusyRef.current) return;
      csvAppendBusyRef.current = true;
    }

    setLoading(true);
    if (currentPage === 1 && contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    getItems(mode, tag, search, platform, price, currentPage, limit)
      .then((result) => {
        const list = result?.items ?? [];
        const tn = result?.totalItemsNumber;
        const totalNum =
          typeof tn === "number" && !Number.isNaN(tn)
            ? tn
            : tn != null && tn !== ""
            ? parseInt(String(tn), 10)
            : NaN;
        const validTotal = !Number.isNaN(totalNum);
        if (currentPage === 1) {
          setTotalItemsNumber(
            tn !== undefined && tn !== null && tn !== "" ? tn : 0
          );
          setFilteredItems(list);
        } else {
          setFilteredItems((prev) => [...prev, ...list]);
        }
        const loadedSoFar = (currentPage - 1) * limit + list.length;
        setHasMore(
          validTotal ? loadedSoFar < totalNum : list.length === limit
        );
      })
      .catch(() => {
        if (currentPage === 1) {
          setFilteredItems([]);
          setTotalItemsNumber(0);
        }
        setHasMore(false);
      })
      .finally(() => {
        if (currentPage > 1) csvAppendBusyRef.current = false;
        setLoading(false);
      });
  };

  const filterItemsRef = useRef(filterItems);
  filterItemsRef.current = filterItems;

  useLayoutEffect(() => {
    if (loading || !hasMore) return;
    const fd = formDataRef.current;
    if (fd.tag === "Favs") return;
    const n = filteredItems.length;
    if (n === 0 || n % ITEMS_PER_PAGE !== 0) return;
    const sc = contentRef.current;
    if (!sc) return;
    const gap = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (gap > 520) return;
    const nextPage = n / ITEMS_PER_PAGE + 1;
    filterItemsRef.current(
      "apps",
      fd.tag,
      fd.search,
      [fd.check],
      [fd.radio],
      nextPage,
      ITEMS_PER_PAGE
    );
    setPage(nextPage);
  }, [filteredItems.length, loading, hasMore]);

  useEffect(() => {
    if (loadMoreInView && !loading && hasMore) {
      setPage((prev) => {
        const next = prev + 1;
        filterItems(
          "apps",
          formData.tag,
          formData.search,
          [formData.check],
          [formData.radio],
          next,
          ITEMS_PER_PAGE
        );
        return next;
      });
    }
  }, [loadMoreInView, loading, hasMore]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formData.tag !== "Favs") return;
    filterItems(
      "apps",
      formData.tag,
      formData.search,
      [formData.check],
      [formData.radio],
      1,
      ITEMS_PER_PAGE
    );
  }, [favsItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const memorizedCard = useMemo(() => {
    return filteredItems?.map((item, index) => (
      <LazyItemCard
        key={item._id || index}
        item={item}
        selectedMode="apps"
        isFavorite={favPbIds.some((fid) => favoriteIdMatches(fid, item))}
        toogleLike={toogleLike}
      />
    ));
  }, [favPbIds, filteredItems]);

  return (
    <Layout
      leftBar={<HomeLeftBar selectedMode="apps" />}
      centerBar={
        <HomeCenterBar
          formData={formData}
          favsNumber={favPbIds?.length}
          debouncedSearch={debouncedSearch}
          handleFormChange={handleFormChange}
          handleClearFilters={handleClearFilters}
          selectedMode="apps"
          itemsNumber={filteredItems?.length}
          totalItemsNumber={totalItemsNumber}
          setDebouncedSearch={setDebouncedSearch}
          icon="apps"
        />
      }
      selectedMode="apps"
      contentRef={setContentWrapperNode}
    >
      <div className="cards-grid">{memorizedCard}</div>
      <div ref={loadMoreRef} className="end-of-content-box">
        {!loading && !hasMore && filteredItems?.length > 0 && (
          <div className="end-of-content">
            <SvgIcn name="check" className="shadow-icon" />
            <p>
              You've seen it all! <br /> No more items to load.
            </p>
          </div>
        )}
        {!loading && filteredItems?.length === 0 && (
          <div className="end-of-content">
            <SvgIcn name="no_item" className="shadow-icon" />
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Apps;